import React, { useEffect, useState } from "react";
import { ComponentLoader } from "./Loader";
import styles from "./styles.module.scss";
import { useLineageContext } from "../context/LineageProvider";
import { Stack } from "@altimateai/lego";
import { ApiHelper } from "../../api/index";

import { Button, Typography } from "@altimateai/lego";
import {
  IconBook,
  IconCalendar,
  IconEye,
  IconHeartRateMonitor,
  IconLibraryFilled,
  IconPremiumRights,
  IconRefresh,
  IconServer,
  IconSitemap,
  IconUser,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";

interface DashboardData {
  rk: string;
  dashboard: string;
  workbook: string;
  owner: string;
  refreshFrequency: string;
  insights: Array<{
    name: string;
    description: string;
  }>;
  views_30d: number;
  refreshes_30d: number;
  views: number;
  refreshes: number;
  tableauTags: string[];
  site_name: string;
  project_name: string;
  last_viewed: string;
  user_count: number;
  instance_name: string;
  total_cost: number;
  cost_attribution_type: string | null;
  instrumentation_snippet: string | null;
  dashboard_url: string;
}

// Color mapping for tags
const COLOR_MAP: Record<string, string> = {
  blue: "#247efe",
  orange: "#ff754c",
  red: "#ee140f",
  green: "#01cd8c",
  dark_green: "#19504d",
  yellow: "#783a00",
  purple: "#392091",
};

const COLOR_BG_MAP: Record<string, string> = {
  blue: "rgba(36, 126, 254, 0.15)",
  orange: "rgba(255, 117, 76, 0.15)",
  red: "rgba(238, 20, 15, 0.15)",
  green: "rgba(1, 205, 140, 0.15)",
  dark_green: "#d3f8f8",
  yellow: "#fff4c3",
  purple: "#e6ddff",
};

// Available colors for hash-based selection
const AVAILABLE_COLORS = ["blue", "orange", "red", "green", "yellow", "purple"];

// Simple hash function to convert string to consistent color
const getHashColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  const index = Math.abs(hash) % AVAILABLE_COLORS.length;
  return AVAILABLE_COLORS[index];
};

// Local ColorTag component following altimate-components standards
const LocalColorTag = ({ label, color = "blue" }: { label: string; color?: string }) => {
  const backgroundColor = COLOR_BG_MAP[color] || COLOR_BG_MAP.blue;
  const textColor = COLOR_MAP[color] || COLOR_MAP.blue;

  return (
    <div
      className="al-inline-flex al-items-center al-px-3 al-py-1 al-rounded-full al-whitespace-nowrap al-text-xs al-font-medium al-truncate al-bg-background al-leading-5"
      style={{
        backgroundColor,
        color: textColor,
      }}
    >
      {label}
    </div>
  );
};

// Custom Row Component for same-line title and value display
const CustomRow = ({ title, value }: { title: React.ReactNode; value: React.ReactNode }) => (
  <div className="al-px-3 al-py-4 al-rounded-lg al-border al-border-gray-200 al-bg-background">
    <Stack direction="row" align="center" justify="between" gap="md">
      <div className="al-flex-shrink-0">{title}</div>
      <div className="al-text-right al-text-sm al-text-gray-600">{value}</div>
    </Stack>
  </div>
);

/**
 * Component to display dashboard details
 */
const DashboardDetails = ({
  onNavigate,
}: {
  onNavigate?: (navigationInfo: { url?: string; id?: string; table: string }) => void;
} = {}) => {
  const {
    state: { selectedTable },
  } = useLineageContext();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!selectedTable) return;
    setIsLoading(true);

    ApiHelper.get("getDashboardById", { dashboard_id: selectedTable })
      //@ts-expect-error just for now
      .then((_data: DashboardData) => {
        setData(_data);
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error fetching dashboard details:", error);
        setData(null);
        setIsLoading(false);
      });
  }, [selectedTable]);

  if (isLoading || !data || !selectedTable) return <ComponentLoader />;

  // Format cost value
  const formatCost = (cost: number) => {
    if (cost >= 1000) {
      return `$${(cost / 1000).toFixed(1)}k`;
    }
    return `$${cost.toFixed(2)}`;
  };

  // Format large numbers
  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toLocaleString();
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Stack direction="column" gap="md" className="al-p-4 al-h-full al-overflow-y-auto">
      {/* Header */}
      <div className={styles.table_details_header}>
        <IconHeartRateMonitor className="al-w-6 al-h-6 al-text-blue-500" />
        <Stack direction="row" align="center">
          <div className="al-font-semibold al-text-lg al-line-clamp-2 al-w-full">
            {data.dashboard}
          </div>
          {data.total_cost != null && (
            <Stack justify="end" className="al-w-full">
              <Button
                size="sm"
                onClick={() => {
                  if (!onNavigate || !data) return;
                  onNavigate({
                    url: `/query/tableau-jobs/${encodeURIComponent(data.rk)}`,
                    id: data.rk,
                    table: data.dashboard,
                  });
                }}
              >
                Cost Analysis
              </Button>
            </Stack>
          )}
        </Stack>
      </div>

      {data.insights && data.insights.length > 0 && (
        <div className="al-mt-3">
          <Stack direction="row" gap="xs" className="al-flex-wrap al-my-3">
            {data.insights.map((insight, index) => (
              <LocalColorTag label={insight.name} color={getHashColor(insight.name)} key={index} />
            ))}
          </Stack>
        </div>
      )}

      {/* Dashboard Details */}
      <div className="al-flex-grow al-rounded-lg al-p-4 al-bg-gray-100">
        <Stack direction="column" gap="md">
          <Typography variant="caption" className="al-font-semibold">
            Dashboard Details
          </Typography>
          {data.total_cost !== null && (
            <CustomRow
              title={
                <Stack direction="row" align="center" gap="xs">
                  <IconPremiumRights className="al-w-4 al-h-4 al-text-blue-600" />
                  <span className="al-font-medium">Total Cost</span>
                </Stack>
              }
              value={<span className="al-font-semibold">{formatCost(data.total_cost)}</span>}
            />
          )}

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconUser className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Owner</span>
              </Stack>
            }
            value={data.owner}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconBook className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Workbook</span>
              </Stack>
            }
            value={data.workbook}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconLibraryFilled className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Project</span>
              </Stack>
            }
            value={data.project_name}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconSitemap className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Site</span>
              </Stack>
            }
            value={data.site_name}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconServer className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Instance</span>
              </Stack>
            }
            value={data.instance_name}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconEye className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Views (30 days)</span>
              </Stack>
            }
            value={<span className="al-font-semibold">{formatNumber(data.views)}</span>}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconRefresh className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Refreshes (30 days)</span>
              </Stack>
            }
            value={data.refreshes.toString()}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconUsers className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Unique Users</span>
              </Stack>
            }
            value={data.user_count.toString()}
          />

          <CustomRow
            title={
              <Stack direction="row" align="center" gap="xs">
                <IconCalendar className="al-w-4 al-h-4 al-text-blue-600" />
                <span className="al-font-medium">Last Viewed</span>
              </Stack>
            }
            value={formatDate(data.last_viewed)}
          />

          {data.dashboard_url && (
            <CustomRow
              title={
                <Stack direction="row" align="center" gap="xs">
                  <IconWorld className="al-w-4 al-h-4 al-text-blue-600" />
                  <span className="al-font-medium">Dashboard URL</span>
                </Stack>
              }
              value={
                <a
                  href={data.dashboard_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="al-text-blue-600 al-underline al-truncate"
                >
                  Open Dashboard
                </a>
              }
            />
          )}
        </Stack>
      </div>
    </Stack>
  );
};

export default DashboardDetails;
